import React, { useEffect, useState } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useTour } from '../contexts/TourContext';
import introSteps from '../tours/introTour';
import { deckTours } from '../tours/deckTours';

const parseHtmlToReact = (text) => {
  if (typeof text !== 'string') return text;

  const aTagRegex = /<a\s+(?:[^>]*?\s+)?href="([^"]*)"[^>]*>(.*?)<\/a>/gi;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = aTagRegex.exec(text)) !== null) {
    const [_, href, linkText] = match;
    const textBefore = text.substring(lastIndex, match.index);

    if (textBefore) {
      parts.push(textBefore);
    }

    parts.push(
      <a
        key={match.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#818cf8', textDecoration: 'underline', fontWeight: 600 }}
      >
        {linkText}
      </a>
    );

    lastIndex = aTagRegex.lastIndex;
  }

  const textAfter = text.substring(lastIndex);
  if (textAfter) {
    parts.push(textAfter);
  }

  return parts.length > 0 ? <span>{parts}</span> : text;
};

const Tour = () => {
  const { activeTour, tourStepIndex, tourRunning, advanceStep, endTour } = useTour();
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    let rawSteps = [];
    if (activeTour === 'intro') {
      rawSteps = introSteps;
    } else if (activeTour?.startsWith('tour-')) {
      const deckTour = deckTours.find(t => t.id === activeTour);
      if (deckTour) {
        rawSteps = deckTour.steps;
      }
    }

    // Process steps to parse HTML strings in content into clickable React links
    const processedSteps = rawSteps.map(step => ({
      ...step,
      content: parseHtmlToReact(step.content)
    }));

    setSteps(processedSteps);
  }, [activeTour]);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === 'close') {
      endTour();
    } else if (['step:after', 'error:target_not_found'].includes(type)) {
      // Update step index to keep it in sync
      const nextStepIndex = index + (action === 'prev' ? -1 : 1);
      advanceStep(nextStepIndex);
    }
  };

  if (!tourRunning || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={tourRunning}
      stepIndex={tourStepIndex}
      callback={handleJoyrideCallback}
      continuous={true}
      showProgress={true}
      showSkipButton={false}
      disableOverlayClose={true}
      spotlightPadding={4}
      styles={{
        options: {
          arrowColor: 'rgba(15, 23, 42, 0.95)',
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          overlayColor: 'rgba(0, 0, 0, 0.4)',
          primaryColor: '#6366f1',
          textColor: '#f8fafc',
          zIndex: 10000,
        },
        spotlight: {
          border: '2px solid #6366f1',
          borderRadius: '12px',
          boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.2), 0 0 15px rgba(99, 102, 241, 0.5)',
        },
        tooltip: {
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '16px',
          fontFamily: "'Outfit', sans-serif",
          padding: '24px',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontSize: '18px',
          fontWeight: 600,
          marginBottom: '12px',
        },
        tooltipContent: {
          fontSize: '14px',
          lineHeight: 1.5,
          color: '#cbd5e1',
          whiteSpace: 'pre-line',
        },
        buttonNext: {
          backgroundColor: '#6366f1',
          borderRadius: '8px',
          padding: '8px 16px',
          fontWeight: 600,
        },
        buttonBack: {
          color: '#94a3b8',
          marginRight: '12px',
        },
        buttonSkip: {
          color: '#94a3b8',
        },
        beaconInner: {
          backgroundColor: '#6366f1',
        },
        beaconOuter: {
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.2)',
        }
      }}
    />
  );
};

export default Tour;
